import { render, h } from 'preact';
import htm from 'htm';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { getBrand } from '../_getBrand';
import { algoliaConfig } from '../_config';
import { getProductTitleParts } from 'swatches';
import { collectionCardTemplate } from '../snippets/collectionCardTemplate';
import { pageCardTemplate } from '../snippets/pageCardTemplate';
import { querySuggestionTemplate } from '../snippets/querySuggestionTemplate';
import { formatMoney } from 'theme';
import {
  buildBadgeHtml,
  buildSaveMessageHtml,
} from '../../scripts/product-card-renderers'
import { mountSRPSearchBox } from './search-box';
import { applyMarketPricing } from '../_marketPricing';
import {
  getFacetStateFromURL,
  serializeFacetStateToURL,
  hasActiveFacets,
  buildFiltersString,
  getDisjunctiveRequests,
  mergeFacetCounts,
  getIndexNameForSort,
  mountFacetPanel,
  mountCurrentFilters,
} from './facets';

const html = htm.bind(h);

/**
 * Mounts the Algolia-powered Search Results Page (SRP).
 *
 * Structure (top to bottom):
 *   1. URL <-> state helpers      - read/write query, page, filters to/from the URL
 *   2. Utilities                  - escaping, pagination math
 *   3. Search                     - runs the multi-index Algolia query
 *   4. Product card building      - turns a single hit into a DOM node
 *   5. Render                     - takes search results and updates the DOM
 *   6. Window events + init       - wires everything up
 *
 * Faceted filtering lives in ./facets.js. This file owns orchestration:
 * it reads facet state from the URL, folds it into the same
 * `searchClient.search()` call as the products/collections/pages
 * requests (including any extra disjunctive-count requests), and hands
 * the results back to the facet panel (and the current-filters chip
 * list) to render. See facets.js for the brandConfig.facets shape this
 * expects.
 */
export async function mountSRP() {
  const template = document.getElementById('product-card-template');
  const mountPoint = document.getElementById('algoliaSRP');
  if (!mountPoint) return; // not on the SRP, bail early

  const brand = getBrand();
  const config = algoliaConfig[brand];
  const facetsConfig = config.facets;
  const searchClient = algoliasearch(
    config.algolia.appId,
    config.algolia.apiKey
  );
  const defaultIndexName = 'shopify_products';

  // Cache DOM references once instead of re-querying on every render call.
  const els = {
    status: document.getElementById('algoliaSRPStatus'),
    productHits: document.getElementById('algoliaSRPProductHits'),
    collectionHits: document.getElementById('algoliaSRPCollectionHits'),
    pageHits: document.getElementById('algoliaSRPPageHits'),
    pagination: document.getElementById('algoliaSRPpagination'),
    facets: document.getElementById('algoliaSRPFacets'),
    currentFilters: document.querySelector('#algoliaSRPCurrentFilters'),
  };

  // Guards against out-of-order responses: if the user paginates/types quickly,
  // an older, slower request could otherwise resolve after a newer one and
  // overwrite the DOM with stale results.
  let latestRequestId = 0;

  // Accumulates swatch metadata (hex/image) by color name across the page's
  // lifetime. Seeded once, unfiltered, via seedColorSwatchMap() at init, and
  // topped up opportunistically by every subsequent runSearch() call — never
  // reset, so a color's swatch data is never lost just because it later
  // filters out of productResults.hits.
  const colorSwatchMap = {};

  // Shared handlers object — the facet panel and the current-filters chip
  // list both need to trigger the exact same state-update path, so they're
  // handed the same functions rather than two separately-built objects.
  const handlers = {
    onFacetChange: handleFacetChange,
    onSortChange: handleSortChange,
    onClear: handleClearChange,
  };

  // Facet panel is mounted once; `updateFacetPanel` is called after every
  // search with fresh counts/state (see mountFacetPanel in facets.js).
  const updateFacetPanel = els.facets
    ? mountFacetPanel(els.facets, facetsConfig, handlers)
    : null;

  // Current-filters chip list is mounted once too; mountCurrentFilters
  // returns a safe no-op update() if els.currentFilters is null (e.g. the
  // liquid snippet isn't present on a given template), so no extra guard
  // is needed at the call site in runSearch().
  const updateCurrentFilters = mountCurrentFilters(els.currentFilters, facetsConfig, handlers);

  // ==== URL <-> STATE HELPERS ===============================

  // return the whole URL as a single object
  function getStateFromURL() {
    const params = new URLSearchParams(window.location.search);

    return {
      query: params.get('q') ?? '',
      page: Math.max(0, parseInt(params.get('page') ?? '1', 10) - 1), // Adjust 1-indexing to 0-indexing as Algolia requires
      sortKey: params.get('sort') ?? '',
      facetState: getFacetStateFromURL(params, facetsConfig),
    }
  }

  function pushStateToURL({ query, page, sortKey, facetState }, replace = false) {
    const params = new URLSearchParams(window.location.search);

    if (query) {
      params.set('q', query);
    } else {
      params.delete('q');
    }

    if (page > 0) {
      // Store page as 1-indexed in the URL for readability
      params.set('page', String(page + 1));
    } else {
      params.delete('page');
    }

    if (sortKey) {
      params.set('sort', sortKey);
    } else {
      params.delete('sort');
    }

    serializeFacetStateToURL(params, facetState, facetsConfig);

    const newURL = `${window.location.pathname}?${params.toString()}`;
    const fn = replace ? 'replaceState' : 'pushState';
    window.history[fn]({ query, page, sortKey, facetState }, '', newURL);
  }

  // ==== UTILITIES ============================================

  // Escapes the characters that matter inside HTML text/attribute content.
  // Note: only handles &, <, >, " — safe for all current usage since every
  // attribute in this file is double-quoted, but not a general-purpose
  // sanitizer (doesn't escape single quotes).
  function escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Returns an array of 0-indexed page numbers with '…' gap markers,
   * showing first page, last page, current page, and 1 page either side.
   */
  function getPaginationRange(current, total) {
    const delta = 1;
    const range = new Set([0, total - 1]);
    for (let i = Math.max(0, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      range.add(i);
    }
    const sorted = [...range].sort((a, b) => a - b);
    const result = [];
    let prev = -1;
    for (const p of sorted) {
      if (p - prev > 1) result.push('…');
      result.push(p);
      prev = p;
    }
    return result;
  }

  // Cached once: the chevron icons rendered by snippets/icon.liquid via the
  // hidden #algolia-pagination-icons template, so the JS-built pagination
  // reuses the same markup as snippets/pagination.liquid instead of
  // hardcoding its own arrows.
  let paginationIcons = null;
  function getPaginationIcons() {
    if (paginationIcons) return paginationIcons;
    const template = document.getElementById('algolia-pagination-icons');
    paginationIcons = {
      prev: template?.content.querySelector('[data-icon="chevron-left"]')?.innerHTML || '&larr;',
      next: template?.content.querySelector('[data-icon="chevron-right"]')?.innerHTML || '&rarr;',
    };
    return paginationIcons;
  }

  // ==== SWATCH METADATA SEED ==================================

  /**
   * One-time, unfiltered fetch to populate colorSwatchMap. Without
   * this, any color swatch not on the first page of results or within 
   * the filtered results will not show up in the filter options.
   */
  async function seedColorSwatchMap() {
    try {
      let page = 0;
      let nbPages = 1;

      while (page < nbPages) {
        const { results } = await searchClient.search({
          requests: [
            {
              indexName: defaultIndexName,
              query: '',
              hitsPerPage: 1000,
              page,
              attributesToRetrieve: ['meta.filter.swatch'],
            },
          ],
        });

        const result = results[0];

        nbPages = result.nbPages;

        for (const hit of result.hits) {
          const swatches = hit.meta?.filter?.swatch || [];

          for (const swatch of swatches) {
            colorSwatchMap[swatch.fields.name] = {
              color: swatch.fields.color,
              image: swatch.fields.image,
            };
          }
        }

        page++;
      }

    } catch (err) {
      console.error('[SRP] Swatch map seed failed:', err);
      // Non-fatal — colorSwatchMap just stays partially populated and
      // keeps filling in opportunistically as runSearch() sees more hits.
    }
  }

  // ==== NO RESULTS ===========================================

  async function fetchNoResultsContent(query) {
    if (!config.noResults) return { taggedProducts: [] };

    const requests = [];
    const requestTypes = [];

    if (config.noResults.taggedProducts?.enabled) {
      requests.push({
        indexName: 'shopify_products',
        query: '',
        filters: config.autocomplete.taggedProducts.filter,
        hitsPerPage: config.noResults.taggedProducts.hitsPerPage ?? 8,
        page: 0,
      });
      requestTypes.push('taggedProducts');
    }

    if (!requests.length) return { taggedProducts: [] };

    try {
      const { results } = await searchClient.search({ requests });
      const out = { taggedProducts: [] };
      results.forEach((res, i) => { out[requestTypes[i]] = res.hits; });
      if (out.taggedProducts.length) await applyMarketPricing(out.taggedProducts);
      return out;
    } catch (err) {
      console.error('[SRP] No-results content fetch failed:', err);
      return { taggedProducts: [] };
    }
  }

  function toggleNoResultsUI(isNoResults) {
    if (els.facets) els.facets.hidden = isNoResults;
    if (els.currentFilters) els.currentFilters.hidden = isNoResults;
  }

  function renderNoResults({ taggedProducts }) {
    document.querySelector('.srp-layout--results').hidden = true;
    document.querySelector('.srp-layout--no-results').hidden = false;

    document.querySelector('.algolia-srp-search').classList.add('algolia-srp-search--no-results')

    els.productHits.innerHTML = '';
    const results = document.querySelector('.algolia-srp-results');
    results.classList.add('algolia-srp-no-results');
    results.classList.remove('algolia-srp-results');

    /**
     * Categories Section
     */
    if (config.noResults.categories?.enabled) {
      document.querySelector('.srp-no-results-categories').hidden = false;
      document.getElementById('srpCategory1').innerHTML = config.noResults.categories.link1;
      document.getElementById('srpCategory2').innerHTML = config.noResults.categories.link2;
      document.getElementById('srpCategory3').innerHTML = config.noResults.categories.link3;
      document.getElementById('srpCategory4').innerHTML = config.noResults.categories.link4;
      document.getElementById('srpCategory5').innerHTML = config.noResults.categories.link5;
    }

    /**
     * Help Section
     */
    if (config.noResults.help?.enabled) {
      document.querySelector('.srp-no-results-help').hidden = false;
      document.getElementById('srpHelpLine1').innerHTML = config.noResults.help.line1;
      document.getElementById('srpHelpLine2').innerHTML = config.noResults.help.line2;
      document.getElementById('srpHelpLine3').innerHTML = config.noResults.help.line3;
    }

    /**
     * Bestsellers Section
     */
    if (taggedProducts.length) {
      const hitsContainer = document.getElementById('algoliaSRPNoResultsHits');
      const layout = document.querySelector('.srp-layout--no-results');
      const grid = document.createElement('div');
      const cta = document.createElement('a');

      hitsContainer.innerHTML = `<h2 class="h2 text-center">${config.noResults.taggedProducts?.heading}</h2>`;
      cta.innerHTML = config.noResults.taggedProducts?.linkName;
      grid.className = 'srp-no-results__grid';
      cta.className = 'button';
      cta.href = config.noResults.taggedProducts?.link;

      taggedProducts.forEach(hit => grid.appendChild(buildProductCard(hit)));

      hitsContainer.appendChild(grid);
      hitsContainer.appendChild(cta);
      layout.appendChild(hitsContainer);
    }

    els.productHits.appendChild(wrap);
  }


  // ==== SEARCH ===============================================

  async function runSearch({ query, page, sortKey, facetState }) {
    const requestId = ++latestRequestId;
    setLoadingState(true);

    const productIndexName = getIndexNameForSort(sortKey, facetsConfig, defaultIndexName);
    const filters = buildFiltersString(facetState, facetsConfig);

    // Any disjunctive attribute with an active selection needs its own
    // "counts as if this attribute weren't filtering" request. Attributes
    // with no active selection are already correct from the main request,
    // so this list — and therefore the request count — grows and shrinks
    // with how many filters are actually in use.
    const { requests: disjunctiveRequests, attributes: disjunctiveAttributes } = getDisjunctiveRequests({
      query,
      facetState,
      facetsConfig,
      indexName: productIndexName,
    });

    try {
      const { results } = await searchClient.search({
        requests: [
          {
            indexName: productIndexName,
            query,
            hitsPerPage: 24,
            page: page,
            facets: ['*'], // intentional — returns all facets/facets_stats for every configured attribute
            filters: filters || undefined,
          },
          {
            indexName: 'shopify_collections',
            query,
            hitsPerPage: 24,
            page: 0,
          },
          {
            indexName: 'shopify_pages',
            query,
            hitsPerPage: 24,
            page: 0,
          },
          ...disjunctiveRequests,
        ],
      });

      // A newer search has started since this one was kicked off — drop
      // these results rather than let them clobber the current view.
      if (requestId !== latestRequestId) return;

      const [productResults, collectionResults, pageResults, ...disjunctiveResults] = results;

      // Overlay real current-market prices before anything renders — see
      // _marketPricing.js for why the indexed price/compare_at_price can't
      // be trusted as-is.
      await applyMarketPricing(productResults.hits);

      // Another await point — re-check in case a newer search landed while
      // pricing was in flight.
      if (requestId !== latestRequestId) return;

      for (const hit of productResults.hits) {
        const swatches = hit.meta?.filter?.swatch || [];

        for (const swatch of swatches) {
          colorSwatchMap[swatch.fields.name] = {
            color: swatch.fields.color,
            image: swatch.fields.image,
          };
        }
      }

      const isNoResults = Boolean(query) && productResults.nbHits === 0;

      renderStatus(productResults, query);       // already renders "No results for..."
      renderCollectionHits(collectionResults);   // hides itself if empty
      renderPageHits(pageResults);               // hides itself if empty
      renderPagination(productResults, page);    // hides itself when nbPages <= 1
      toggleNoResultsUI(isNoResults);

      if (isNoResults) {
        fetchNoResultsContent(query).then(content => {
          if (requestId !== latestRequestId) return; // stale-response guard
          renderNoResults(content);
        });
      } else {
        renderProductHits(productResults);
        updateCurrentFilters({ facetState });

        if (updateFacetPanel) {
          const { counts, stats } = mergeFacetCounts({
            mainFacets: productResults.facets,
            mainFacetsStats: productResults.facets_stats,
            disjunctiveResults,
            attributes: disjunctiveAttributes,
            facetsConfig,
          });
          updateFacetPanel({ facetState, counts, stats, sortKey, colorSwatchMap });
        }
      }
    } catch (err) {
      console.error('[SRP] Search failed:', err);
      // TODO: render a user-facing error state
    } finally {
      if (requestId === latestRequestId) setLoadingState(false);
    }
  }

  function setLoadingState(isLoading) {
    mountPoint.setAttribute('data-loading', String(isLoading));
  }

  // ==== FACET / SORT EVENT HANDLERS ===========================

  // Any facet or sort change resets pagination to page 0 — the previous
  // page number is meaningless against a newly filtered result set.

  function handleFacetChange(attr, nextValue) {
    const state = getStateFromURL();

    state.facetState = {
      ...state.facetState,
      [attr]: nextValue,
    };

    state.page = 0;

    pushStateToURL(state);
    runSearch(state);
  }

  function handleSortChange(nextSortKey) {
    const state = getStateFromURL();

    state.sortKey = nextSortKey;
    state.page = 0;

    pushStateToURL(state);
    runSearch(state);
  }

  function handleClearChange() {
    const nextFacetState = {};

    for (const [attr, def] of Object.entries(facetsConfig.attributes)) {
      nextFacetState[attr] =
        def.type === 'range'
          ? { min: null, max: null }
          : [];
    }

    const state = getStateFromURL();

    state.facetState = nextFacetState;
    state.page = 0;

    pushStateToURL(state);
    runSearch(state);
  }

  // ==== PRODUCT CARD BUILDING ================================

  /**
   * renderBadges()
   *
   * Renders product badges (custom, sold_out, discount) based on product data and settings.
   * Core logic is coming from product-card-renderers.js.
   *
   * ***MUST*** match logic in product-badges.liquid.
   */
  function renderBadges(card, hit) {
    const badgeList = card.querySelector('badge-list');
    if (!badgeList) return;
    if (document.body.dataset.b2b === "true") return;

    const { settings, strings } = window.themeVariables;
    const html = buildBadgeHtml({
      product: hit,
      productTags: hit.tags,
      settings,
      strings,
      isB2B: Shopify.customer?.b2b === true,
    });

    badgeList.innerHTML = html;
    badgeList.toggleAttribute('hidden', html.trim() === '');
  }

  /**
   * renderSaveMessages()
   *
   * Renders save-message elements based on product tags, settings, and metafields.
   * Core logic is coming from product-card-renderers.js.
   *
   * ***MUST*** match logic in product-card.liquid.
   */
  function renderSaveMessages(card, hit) {
    const container = card.querySelector('[data-save-messages]');
    if (!container) return;
    if (document.body.dataset.b2b === "true") return;

    const { settings, strings } = window.themeVariables;
    const html = buildSaveMessageHtml({
      product: hit,
      productTags: hit.tags,
      settings,
      strings,
      isB2B: Shopify.customer?.b2b === true,
      userRegion: settings.localizationCountry || Shopify.country,
    });

    container.innerHTML = html;
    container.toggleAttribute('hidden', html.trim() === '');
  }

  // Builds the price markup for a non-color-separated product card.
  //
  // Multipack/bundle products (title contains 'Multi-') don't have a fixed
  // price since it depends on component selection — the indexed price is
  // often 0. Show "Price Varies" instead, same as collection/PLP cards.
  // ***MUST*** match the multipack_hide_price logic in price-list.liquid.
  function populatePrice(card, hit) {
    const priceWrapper = card.querySelector('.price-list');
    if (!priceWrapper) return;

    if (document.body.dataset.b2b === "true") {
      priceWrapper.innerHTML = `
        <p class="h5">View Product for Special Pricing</p>
      `;

    } else if (hit.title && hit.title.includes('Multi-')) {
      priceWrapper.innerHTML = `
        <sale-price class="h5">
          <span class="sr-only">Price</span>Price Varies
        </sale-price>
      `;

    } else if (hit.compare_at_price) {
      priceWrapper.innerHTML = `
        <compare-at-price class="h5 text-subdued line-through">
          <span class="sr-only">Regular price</span>${formatMoney(hit.compare_at_price * 100)} ${Shopify.currency.active}
        </compare-at-price>
        <sale-price class="h5 text-on-sale">
          <span class="sr-only">Sale price</span>${formatMoney(hit.price * 100)} ${Shopify.currency.active}
        </sale-price>
      `;

    } else {
      priceWrapper.innerHTML = `
        <sale-price class="h5">
          <span class="sr-only">Price</span>${formatMoney(hit.price * 100)} ${Shopify.currency.active}
        </sale-price>
      `;

    }
  }

  // Wires up the swatch placeholder radio/label pair with unique ids scoped
  // to this card's swatch group.
  function populateSwatchPlaceholder(card, swatchIdPrefix, swatchName) {
    const placeholderInput = card.querySelector('input[type="radio"]');
    const placeholderLabel = card.querySelector('label[for="product-card-swatch-placeholder"]');
    if (!placeholderInput || !placeholderLabel) return;

    const placeholderId = `${swatchIdPrefix}-placeholder`;
    placeholderInput.name = swatchName;
    placeholderInput.id = placeholderId;
    placeholderLabel.htmlFor = placeholderId;
  }

  // Builds a single product card element for a search hit.
  function buildProductCard(hit) {
    const [title, color] = getProductTitleParts(hit.title);
    const isSeparatedProduct = Boolean(color); // true when the product has a distinct color variant

    const productUrl = `/products/${hit.handle}`;
    const swatchName = `Color-${hit.objectID}`;
    const swatchIdPrefix = `product-card-swatch-${hit.objectID}`;

    const wrap = document.createElement('div');
    wrap.className = 'search-result';
    wrap.dataset.objectid = hit.objectID;

    const sample = template?.content?.querySelector(isSeparatedProduct ? 'variant-product-card' : 'product-card');
    const card = sample ? sample.cloneNode(true) : document.createElement(isSeparatedProduct ? 'variant-product-card' : 'product-card');

    card.setAttribute('handle', hit.handle);
    if (title) card.setAttribute('product-title', title);
    if (productUrl) card.setAttribute('product-url', productUrl);
    if (color) card.setAttribute('current-color', color);
    if (hit.queryBySale) card.setAttribute('query-by-sale', hit.queryBySale);

    const titleLink = card.querySelector('.product-title');
    if (titleLink) {
      // Modern Ambition splits variant color out after the title, matching
      // the `show_colorname` markup in product-card.liquid (.product-title__garment / .product-title__color).
      // The color part carries the garment title as a prefix (e.g. "T-Shirt Black"),
      // same as buildSwatch()'s publicColorName in swatches.js, so strip it back out for display.
      if (brand === 'ma' && color) {
        const publicColor = color.replace(title, '').trim();
        titleLink.innerHTML = `<span class="product-title__garment">${escapeHTML(title)}</span><span class="product-title__color"><span class="sr-only"> - </span>${escapeHTML(publicColor)}</span>`;
      } else {
        titleLink.textContent = title;
      }
      titleLink.href = productUrl;
    }

    const mediaLink = card.querySelector('.product-card__media');
    if (mediaLink) {
      mediaLink.href = productUrl;
    }

    const swatchFieldset = card.querySelector('[data-swatches]');
    if (swatchFieldset) {
      swatchFieldset.dataset.name = swatchName;
      swatchFieldset.dataset.swatchIdPrefix = swatchIdPrefix;
    }

    populateSwatchPlaceholder(card, swatchIdPrefix, swatchName);
    populatePrice(card, hit);

    const klaviyoWidget = card.querySelector('.klaviyo-star-rating-widget');
    if (klaviyoWidget) {
      klaviyoWidget.dataset.id = hit.id;
      if (title) klaviyoWidget.dataset.productTitle = title;
      if (hit.product_type) klaviyoWidget.dataset.productType = hit.product_type;
    }

    // Color-separated ("gamma-waves") products get their images/badges
    // from the variant-level markup instead, so skip manual population here.
    if (!isSeparatedProduct) {
      card.querySelector('[data-card-image="primary"]').src = hit.product_image || hit.image;
      card.querySelector('[data-card-image="secondary"]').src = hit.product_image || hit.image;

      renderBadges(card, hit);
      renderSaveMessages(card, hit);
    }

    wrap.appendChild(card);
    return wrap;
  }

  // ==== ACCESSIBILITY =========================================
  /**
   * DOM modifications to enhance accessibility and user experience.
   * - remove button (encased in label) that was placed ahead of the input
   * - replace the button (encased in label) after the input
   * 
   * TODO - SOMETHING IS NOT WORKING HERE, ABANDONED TO FOCUS ON 
   * OTHER PRIORITY ITEMS TO GET THIS OUT THE DOOR
   */
  function accessibilityModifications() {
    // const searchBox = document.getElementById('algoliaSRPAutocomplete');
    // const searchBoxPrefix = searchBox.querySelector('.aa-InputWrapperPrefix');
    // const searchBoxSuffix = searchBox.querySelector('.aa-InputWrapperSuffix');
    // const searchBoxOldLabel = searchBox.querySelector('.aa-Label');
    // const submitSVG = `<svg class="aa-SubmitIcon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z"></path></svg>`
    // let SB_domLabel = document.createElement("label");
    // let SB_domSubmit = document.createElement("button");

    // console.log(searchBox);
    // console.log(searchBoxPrefix);
    // console.log(searchBoxSuffix);
    // console.log(searchBoxOldLabel);

    // searchBoxOldLabel.remove;
    // searchBoxPrefix.append(SB_domLabel);
    // searchBoxSuffix.before(SB_domSubmit);
  }

  // ==== RENDER ================================================

  function renderStatus(results, query) {
    const nbHits = Number(results.nbHits) + Number(results.nbPages);

    if (!els.status) return;
    if (!query) { els.status.innerHTML = ''; return; }
    if (nbHits > 0) {
      els.status.innerHTML = `Showing <strong>${nbHits}</strong> results for: <em>〝${escapeHTML(query)}〞</em>`;
      document.title = `Search: ${nbHits} Results | ${config.titleSuffix}`;
    } else {
      els.status.innerHTML = `No results for: <em>〝${escapeHTML(query)}〞</em>`;
    }
  }

  function renderProductHits(results) {
    if (!els.productHits) return;

    // Build off-DOM, then insert once — avoids a reflow per card.
    const fragment = document.createDocumentFragment();
    for (const hit of results.hits) {
      fragment.appendChild(buildProductCard(hit));
    }

    els.productHits.innerHTML = '';
    els.productHits.appendChild(fragment);
  }

  function renderCollectionHits(results) {
    if (!els.collectionHits) return;

    if (!results.hits.length) { els.collectionHits.hidden = true; return; }

    els.collectionHits.hidden = false;
    render(
      html`
        <div>
          <h2 class="h5">Collections</h2>
          <ul>
            ${results.hits.map(hit => html`
              <li>${collectionCardTemplate(hit)}</li>
            `)}
          </ul>
        </div>
      `,
      els.collectionHits
    );
  }

  function renderPageHits(results) {
    if (!els.pageHits) return;

    if (!results.hits.length) { els.pageHits.hidden = true; return; }

    els.pageHits.hidden = false;
    render(
      html`
        <div>
          <h2 class="h5">Pages</h2>
          <ul>
            ${results.hits.map(hit => html`
              <li>${pageCardTemplate(hit)}</li>
            `)}
          </ul>
        </div>
      `,
      els.pageHits
    );
  }

  // Mirrors the markup/a11y semantics of snippets/pagination.liquid: a link
  // (here, a button driven by pushState) per reachable page, a non-interactive
  // <span aria-current="page"> for the current page and for '…' gaps, and
  // prev/next controls that are omitted entirely rather than rendered disabled.
  function renderPagination(results, currentPage) {
    if (!els.pagination) return;

    const { nbPages } = results;
    if (nbPages <= 1) { els.pagination.hidden = true; return; }

    els.pagination.hidden = false;
    const pages = getPaginationRange(currentPage, nbPages);
    const icons = getPaginationIcons();
    const goToPageLabelTemplate = els.pagination.dataset.i18nGoToPage || 'Go to page __INDEX__';
    const goToPageLabel = (displayedPage) => escapeHTML(goToPageLabelTemplate.replace('__INDEX__', displayedPage));

    const prevHTML = currentPage > 0
      ? `<button type="button" class="pagination__link h6" rel="prev" aria-label="${goToPageLabel(currentPage)}" data-page="${currentPage - 1}">${icons.prev}</button>`
      : '';

    const partsHTML = pages.map(p => {
      if (p === '…') {
        return `<span class="pagination__link pagination__link--disabled h6">…</span>`;
      }
      if (p === currentPage) {
        return `<span class="pagination__link pagination__link--disabled h6" aria-current="page">${p + 1}</span>`;
      }
      return `<button type="button" class="pagination__link h6" data-page="${p}" aria-label="${goToPageLabel(p + 1)}">${p + 1}</button>`;
    }).join('');

    const nextHTML = currentPage < nbPages - 1
      ? `<button type="button" class="pagination__link h6" rel="next" aria-label="${goToPageLabel(currentPage + 2)}" data-page="${currentPage + 1}">${icons.next}</button>`
      : '';

    els.pagination.innerHTML = `${prevHTML}${partsHTML}${nextHTML}`;

    els.pagination.querySelectorAll('button[data-page]').forEach(btn => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const newPage = parseInt(btn.dataset.page, 10);
        const state = { ...getStateFromURL(), page: newPage };

        pushStateToURL(state);
        runSearch(state);

        document.querySelector('#algoliaSRPStatus').scrollIntoView({ block: 'start', behavior: 'auto' });
      });
    });
  }

  // ==== WINDOW EVENTS ==========================================

  window.addEventListener('popstate', () => {
    runSearch(getStateFromURL());
  });

  // ==== ON-PAGE SEARCHBOX =====================================
  mountSRPSearchBox({
    onQueryChange(newQuery) {
      const state = { ...getStateFromURL(), query: newQuery, page: 0 };
      pushStateToURL(state);
      runSearch(state);
    },
  });

  // ==== INIT ===================================================
  // replaceState on init so the initial URL is canonical before the first push

  const initialState = getStateFromURL();
  pushStateToURL(initialState, true);
  await seedColorSwatchMap(); // fire-and-forget — runSearch's own accumulation covers any gap until this resolves
  return runSearch(initialState);
}